import { Box, TableCell, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { RecentRecommendationsTableBodyProps, TableBodyData } from "../../types";
import { checkImageExists } from "../../../../utils/utilityFunctions";

// Define proper type for recent recommendations data
interface RecentRecommendationData {
  id: string | number;
  schemeName: string;
  status: string;
  transactionType: string;
  amount: string | number;
  recommendedUnits: string | number;
  date: string;
  amcId?: string;
}

export default function RecentRecommendationsTableBody(
  {
    tableBodyData,
    tableBodyRowCustomStyles,
  }: RecentRecommendationsTableBodyProps
): JSX.Element {
  const theme = useTheme()

  return (
    <>
      {tableBodyData.map((obj: TableBodyData) => {
        const amcIds = ['BDOTR', 'BPIAMC']
        const checkAmcId = amcIds.includes(obj.amcId ?? '') ? obj.amcId?.toLowerCase() : obj.amcId;
        return (
          <TableRow
            key={obj.id}
            sx={{
              '& td': {
                paddingTop: "15px",
                paddingBottom: "15px",
                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
              },
              ...tableBodyRowCustomStyles
            }}
          >
            {obj.schemeName && (
              <TableCell
                align="left"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: '8px' }}>
                  <img
                    src={checkImageExists(checkAmcId) ? `../../../../../../images/amcIcons/${checkAmcId}.svg` : `../../../../../../images/amcIcons/default.svg`}
                    alt={obj.amcId}
                    width={35}
                    height={35}
                    style={{
                      objectFit: "contain",
                      borderRadius: "8px"
                    }}
                  />
                  <Typography variant="body_500" sx={(theme: Theme) => ({
                    color: ""
                  })}>{obj?.schemeName}</Typography>

                </Box>
              </TableCell>
            )}
            <TableCell align="center">
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary
                })}
              >
                {obj?.status ?? '-'}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary
                })}
              >
                {obj?.transactionType ?? '-'}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary
                })}
              >
                {obj?.amount ?? 0}
              </Typography>
            </TableCell>

            <TableCell align="center">
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary
                })}
              >
                {obj?.recommendedUnits ?? 0}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary
                })}
              >
                {obj?.date ?? '-'}
              </Typography>
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}



export function MobileResponsiveRecentRecommendations({
  tableBodyData,
  tableHeaderData,
  
}: {
  tableBodyData: TableBodyData[];
  tableHeaderData: {
    id: string;
    label: string;
    align: string;
    isSortable: boolean;
    key?: string;
  }[];
 
}): JSX.Element {
  const theme = useTheme();


 
  return (
    <>
      {tableBodyData.map((obj: TableBodyData) => {
        const amcIds = ["BDOTR", "BPIAMC"];
        const checkAmcId = amcIds.includes(obj.amcId ?? "")
          ? obj.amcId?.toLowerCase()
          : obj.amcId;

        return (
          <Box
            key={obj.id}
            sx={{
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "16px",
              backgroundColor: theme.palette.common.white,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
            }}
          >

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
                paddingBottom: "12px",
                borderBottom: `1px solid ${theme.palette.grey[100]}`,
              }}
            >
              <Box
                sx={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  width: "35px",
                  height: "35px",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={
                    checkImageExists(checkAmcId)
                      ? `../../../../../../images/amcIcons/${checkAmcId}.svg`
                      : `../../../../../../images/amcIcons/default.svg`
                  }
                  alt={obj.amcId}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body_500"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "",
                    "&:hover": { color: "" },
                  }}
                >
                  {obj?.schemeName ?? ""}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, fontSize: "12px", marginBottom: "4px" }}
                >
                  {tableHeaderData.find((h) => h.id === "status")?.label}
                </Typography>
                <Typography variant="body_500" sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {obj.status ?? "-"}
                </Typography>
              </Box>


              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textalign: "right", textAlign: "right" }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, fontSize: "12px", marginBottom: "4px" }}
                >
                  {tableHeaderData.find((h) => h.id === "buySellType")?.label}
                </Typography>
                <Typography variant="body_500" sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {obj.transactionType ?? "-"}
                </Typography>
              </Box>


              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, fontSize: "12px", marginBottom: "4px" }}
                >
                  {tableHeaderData.find((h) => h.id === "recommendedAmount")?.label}
                </Typography>
                <Typography variant="body_500" sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {obj.amount ?? 0}
                </Typography>
              </Box>


              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, fontSize: "12px", marginBottom: "4px" }}
                >
                  {tableHeaderData.find((h) => h.id === "recommendedUnits")?.label}
                </Typography>
                <Typography variant="body_500" sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {obj.recommendedUnits ?? 0}
                </Typography>
              </Box>


              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, fontSize: "12px", marginBottom: "4px" }}
                >
                  {tableHeaderData.find((h) => h.id === "recommendedDate")?.label}
                </Typography>
                <Typography variant="body_500" sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {obj.date ?? "-"}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
}


